/**
 * Category Master - Grid Screen
 * Refactored to use ERPAdvancedGrid reusable component.
 * Handles data fetching; delegates all grid UI to ERPAdvancedGrid.
 */
import React, { useState, useEffect, useCallback } from 'react';
import ERPAdvancedGrid from '../../../components/erp/ERPAdvancedGrid';
import { erpToast } from '../../../components/erp/ERPToast';
import { API_BASE as API_URL } from '../../../lib/api';


const COLUMNS = [
  { field: 'CatId', header: 'CATEGORY ID', sortKey: 'CatId', visible: true, frozen: false },
  { field: 'CatName', header: 'CATEGORY NAME', sortKey: 'CatName', visible: true, frozen: false },
  { field: 'CatSname', header: 'CATEGORY SNAME', sortKey: 'CatSname', visible: true, frozen: false },
  { field: 'CategoryTypeDisplay', header: 'CATEGORY TYPE', sortKey: 'CategoryType', filterKey: 'CategoryType', visible: true, frozen: false },
  { field: 'SerialNoIMEI', header: 'SERIALNO / IMEICODE', filterKey: 'SerialNo', visible: true, frozen: false },
  { field: 'HSNCode', header: 'HSN CODE', visible: false, frozen: false },
  { field: 'SACCode', header: 'SAC CODE', visible: false, frozen: false },
];

const CategoryMasterGrid = ({ token, onAddNew, onEdit }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (sortField) { params.set('sortField', sortField); params.set('sortDir', sortDir); }
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(`filter${k}`, v); });
      const res = await fetch(`${API_URL}/api/category-master?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) { setData(json.data || []); setTotal(json.total || 0); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, pageSize, sortField, sortDir, filters, token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleVoid = async (row) => {
    if (!window.confirm(`Void category "${row.CatName}"?`)) return;
    await fetch(`${API_URL}/api/category-master/${row.CatId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    loadData();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <ERPAdvancedGrid
      title="Category Master"
      columns={COLUMNS}
      data={data}
      total={total}
      idField="CatId"
      nameField="CatName"
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); }}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      filters={filters}
      onFilterChange={handleFilterChange}
      loading={loading}
      onAddNew={onAddNew}
      onEdit={onEdit}
      onVoid={handleVoid}
      testId="category-master"
      exportFileName="CategoryMaster"
      cellClassName={(field) => {
        if (field === 'CatId') return 'cell-mono';
        if (field === 'CatName') return 'cell-bold';
        if (field === 'CatSname') return 'cell-muted';
        return '';
      }}
    />
  );
};

export default CategoryMasterGrid;
